import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"

// Geocodes an address and populates lat/lng fields + shows a preview map pin
export default class extends Controller {
  static targets = ["addressInput", "latField", "lngField", "mapPreview", "suggestions"]
  static values = { latitude: Number, longitude: Number }

  connect() {
    this.token = document.querySelector('meta[name="mapbox-token"]')?.content
    if (!this.token) return

    mapboxgl.accessToken = this.token
    this.debounceTimer = null

    // Init preview map if we have existing coordinates
    if (this.latitudeValue && this.longitudeValue) {
      this.#initPreviewMap(this.latitudeValue, this.longitudeValue)
    }
  }

  disconnect() {
    this.previewMap?.remove()
  }

  // Called on input event of the address search field
  onInput() {
    clearTimeout(this.debounceTimer)
    const query = this.addressInputTarget.value.trim()
    if (query.length < 4) {
      this.#hideSuggestions()
      return
    }
    this.debounceTimer = setTimeout(() => this.#geocode(query), 400)
  }

  // Called when user selects a suggestion
  selectSuggestion(event) {
    const { lat, lng, label } = event.currentTarget.dataset
    this.addressInputTarget.value = label
    this.latFieldTarget.value = parseFloat(lat)
    this.lngFieldTarget.value = parseFloat(lng)
    // Notify the area-map (if present) to recenter its circle on the new point.
    this.latFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.lngFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.#hideSuggestions()
    this.#initPreviewMap(parseFloat(lat), parseFloat(lng))
  }

  // ── Private ───────────────────────────────────────────────────────────────

  async #geocode(query) {
    if (!this.token) return
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
      `access_token=${this.token}&country=us&types=address,place,region&limit=5`

    try {
      const response = await fetch(url)
      const data = await response.json()
      this.#renderSuggestions(data.features || [])
    } catch {
      this.#hideSuggestions()
    }
  }

  #renderSuggestions(features) {
    if (!features.length) {
      this.#hideSuggestions()
      return
    }

    const list = features.map(f => {
      const [lng, lat] = f.center
      return `<button type="button"
                class="list-group-item list-group-item-action"
                data-action="click->geocoder#selectSuggestion"
                data-lat="${lat}"
                data-lng="${lng}"
                data-label="${f.place_name.replace(/"/g, '&quot;')}">
                ${f.place_name}
              </button>`
    }).join("")

    this.suggestionsTarget.innerHTML = list
    this.suggestionsTarget.classList.remove("d-none")
  }

  #hideSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.innerHTML = ""
      this.suggestionsTarget.classList.add("d-none")
    }
  }

  #initPreviewMap(lat, lng) {
    if (!this.hasMapPreviewTarget) return

    const container = this.mapPreviewTarget
    container.style.height = "220px"
    container.classList.remove("d-none")

    if (this.previewMap) {
      this.previewMap.jumpTo({ center: [lng, lat] })
      this.previewMarker?.setLngLat([lng, lat])
      return
    }

    this.previewMap = new mapboxgl.Map({
      container,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [lng, lat],
      zoom: 11,
      interactive: false,
    })

    this.previewMarker = new mapboxgl.Marker({ color: "#48BA16" })
      .setLngLat([lng, lat])
      .addTo(this.previewMap)
  }
}
